import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Loader2 } from 'lucide-react';
import { useAuth } from '@/stores/auth.store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError } from '@/lib/api/client';

export function RegisterPage() {
  const navigate = useNavigate();
  const register = useAuth((s) => s.register);
  const loading = useAuth((s) => s.loading);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('La password deve essere di almeno 8 caratteri');
      return;
    }
    try {
      await register(email, password, fullName || undefined, inviteCode.trim() || undefined);
      navigate('/setup');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Errore durante la registrazione');
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between bg-primary text-primary-foreground p-12">
        <div className="flex items-center gap-3">
          <Mail className="h-8 w-8" />
          <span className="text-2xl font-bold tracking-tight">Blagomail</span>
        </div>
        <div className="space-y-6">
          <h1 className="text-5xl font-bold leading-tight tracking-tight">Inizia in 30 secondi.</h1>
          <p className="text-lg opacity-90 max-w-md">
            Crea il tuo account, collega la tua casella email e lascia che Claude organizzi la tua inbox per te.
          </p>
        </div>
        <p className="text-sm opacity-70">© 2026 Blagoweb S.r.l.</p>
      </div>

      <div className="flex items-center justify-center p-6">
        <Card className="w-full max-w-md border-0 shadow-none">
          <CardHeader>
            <CardTitle>Crea account</CardTitle>
            <CardDescription>Inserisci i tuoi dati per iniziare.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Nome e cognome</Label>
                <Input id="fullName" type="text" placeholder="Mario Rossi" value={fullName} onChange={(e) => setFullName(e.target.value)} autoFocus />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" placeholder="tu@azienda.it" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" placeholder="Almeno 8 caratteri" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="inviteCode">
                  Codice invito <span className="text-muted-foreground font-normal">(se ne hai uno)</span>
                </Label>
                <Input
                  id="inviteCode"
                  type="text"
                  placeholder="XXXX-XXXX-XXXX"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  className="font-mono"
                />
                <p className="text-xs text-muted-foreground">
                  Senza codice inizi comunque con {' '}
                  <strong>14 giorni di prova</strong>, senza carta di credito.
                </p>
              </div>
              {error && <div className="text-sm text-destructive bg-destructive/10 rounded-md p-3">{error}</div>}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Crea account
              </Button>
              <p className="text-sm text-muted-foreground text-center">
                Hai gia` un account?{' '}
                <Link to="/login" className="text-primary hover:underline font-medium">
                  Accedi
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
